import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigationType } from "react-router-dom";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import "lenis/dist/lenis.css";

import "./fonts.css";
import "./style.css";

import Navbar3 from "./components/Navbar3.jsx";
import IntroLoader from "./components/IntroLoader.jsx";
import { onIntroDone } from "./intro.js";
import Footer from "./components/Footer.jsx";
import HomePage from "./pages/HomePage.jsx";

// the home page ships in the main bundle; the other routes load on demand
const CaseStudySingle = lazy(() => import("./pages/CaseStudySingle.jsx"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));
const CareersPage = lazy(() => import("./pages/CareersPage.jsx"));
const JobPage = lazy(() => import("./pages/JobPage.jsx"));
const LegalPage = lazy(() => import("./pages/LegalPage.jsx"));
const HrApp = lazy(() => import("./pages/hr/HrApp.jsx"));

// opened straight from links in app settings and review forms: no intro animation in the way
const LEGAL_PATHS = new Set(["/privacy-policy", "/terms-of-service"]);

/* The HR admin is its own app: no marketing shell, no smooth scroll, no intro. */
const isHrPath = (pathname) => pathname === "/hr" || pathname.startsWith("/hr/");

const REVEAL_SELECTORS = [
  ".eye-brow", ".heading-wrap", ".value-card", ".capabilities-card",
  ".process-item", ".casestudy-card", ".intergration-main",
  ".testimonial-card", ".pricing-card", ".team-card", ".faq-item",
  ".modal-cta", ".faqs-cta",
];

function useRevealOnScroll() {
  const location = useLocation();

  useEffect(() => {
    const targets = document.querySelectorAll(REVEAL_SELECTORS.join(","));
    if (!("IntersectionObserver" in window) || !targets.length) return;

    targets.forEach((el) => el.classList.add("reveal-on-scroll"));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);
}

gsap.registerPlugin(ScrollTrigger);

/* Scrolls to the element a "#hash" names; smooth when staying on the same page, a jump when arriving from another. */
function scrollToHash(hash, lenis, smooth) {
  const el = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (!el) return;
  // pins on a freshly mounted page add spacing, so measure after they've been laid out
  ScrollTrigger.refresh();
  if (lenis) {
    // Lenis clamps targets to a scroll limit it re-measures on a debounce; after a route change
    // it still holds the previous page's (shorter) limit, which cuts short deep targets like the footer form
    lenis.resize();
    lenis.scrollTo(el, { immediate: !smooth, force: true });
  }
  else el.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
}

/* Lenis smooth scroll driven by GSAP's ticker so ScrollTrigger pins stay in sync */
/*
 * Back/forward return to where the visitor actually was (e.g. the 5th work card), not to the
 * URL's "#work" anchor. The scroll position is stored on the history entry being left.
 */
function useScrollMemory() {
  useEffect(() => {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    const remember = (e) => {
      if (!e.target.closest?.('a[href^="/work/"]')) return;
      history.replaceState({ ...history.state, v3Scroll: window.scrollY }, "");
    };
    document.addEventListener("click", remember, true);
    return () => document.removeEventListener("click", remember, true);
  }, []);
}

/* Restores a remembered scroll position once the page's pins have been laid out. */
function restoreScroll(y, lenis) {
  ScrollTrigger.refresh();
  if (lenis) {
    lenis.resize();
    lenis.scrollTo(y, { immediate: true, force: true });
  } else window.scrollTo(0, y);
}

function useSmoothScroll() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();
  const lenisRef = useRef(null);
  const prevPath = useRef(null);
  const hr = isHrPath(pathname);

  useEffect(() => {
    if (hr || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis();
    lenisRef.current = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    // hold the page still while the intro plays
    lenis.stop();
    const offIntro = onIntroDone(() => lenis.start());
    const tick = (time) => lenis.raf(time * 1000);
    // prioritised so scroll-driven ticker callbacks (e.g. the hero) read this frame's position
    gsap.ticker.add(tick, false, true);
    gsap.ticker.lagSmoothing(0);
    return () => {
      offIntro();
      gsap.ticker.remove(tick);
      gsap.ticker.lagSmoothing(500, 33);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [hr]);

  // "/#section" links land on their section (once the intro has let go of the page); other route changes go to the top
  useEffect(() => {
    const initial = prevPath.current === null;
    const samePage = prevPath.current === pathname;
    prevPath.current = pathname;

    const saved = navType === "POP" ? history.state?.v3Scroll : undefined;
    if (saved != null) {
      let frame = 0;
      const off = onIntroDone(() => {
        // two frames: the remounted page's pins and refresh watchers settle first
        frame = requestAnimationFrame(() => (frame = requestAnimationFrame(() => restoreScroll(saved, lenisRef.current))));
      });
      return () => {
        off();
        cancelAnimationFrame(frame);
      };
    }

    if (!hash) {
      if (initial) return;
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: !samePage });
      else window.scrollTo({ top: 0, behavior: samePage ? "smooth" : "auto" });
      return;
    }

    let frame = 0;
    const off = onIntroDone(() => {
      frame = requestAnimationFrame(() => scrollToHash(hash, lenisRef.current, samePage));
    });
    return () => {
      off();
      cancelAnimationFrame(frame);
    };
  }, [pathname, hash, key, navType]);
}

export default function App() {
  useScrollMemory();
  useSmoothScroll();
  useRevealOnScroll();
  const { pathname } = useLocation();
  // the intro only ever plays on a full page load; job-board visitors and staff shouldn't sit through it
  const [skipIntro] = useState(() => isHrPath(pathname) || pathname === "/careers" || pathname.startsWith("/careers/") || LEGAL_PATHS.has(pathname));

  if (isHrPath(pathname)) {
    return (
      <Suspense fallback={null}>
        <HrApp />
      </Suspense>
    );
  }

  return (
    <div className="page-wrapper">
      <IntroLoader skip={skipIntro} />
      <div className="main-wrapper">
        <Navbar3 />
        <main id="main">
          {/* full-height fallback keeps the footer from flashing up while a route chunk loads */}
          <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/work/:slug" element={<CaseStudySingle />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/:slug" element={<JobPage />} />
              <Route path="/privacy-policy" element={<LegalPage doc="privacy-policy" />} />
              <Route path="/terms-of-service" element={<LegalPage doc="terms-of-service" />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </div>
  );
}
